import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getWhitelists,
  uploadWhitelist,
  updateWhitelist,
  deleteWhitelist,
  getWhitelistVersions,
  type WhitelistsFilters,
} from './whitelistsApi';
import { toast } from '@/utils/toast';
import { useDownloadFile } from '@/hooks/useDownloadFile';

// Query keys
const QUERY_KEYS = {
  whitelists: ['whitelists'] as const,
  whitelistVersions: (whitelistId: number) =>
    ['whitelist-versions', whitelistId] as const,
};

// Hooks for whitelists
export function useWhitelists(
  filters?: WhitelistsFilters,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [...QUERY_KEYS.whitelists, filters],
    queryFn: () => getWhitelists(filters),
    enabled: options?.enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useWhitelistVersions(whitelistId: number, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.whitelistVersions(whitelistId),
    queryFn: () => getWhitelistVersions(whitelistId),
    enabled: enabled && whitelistId > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useUploadWhitelist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadWhitelist,
    onSuccess: () => {
      // Invalidate and refetch whitelists
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.whitelists });
      toast.success('Whitelist uploaded successfully');
    },
    onError: () => {
      toast.error('Failed to upload whitelist');
    },
  });
}

export function useUpdateWhitelist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) =>
      updateWhitelist(id, file),
    onSuccess: () => {
      // Invalidate and refetch whitelists
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.whitelists });
      toast.success('Whitelist updated successfully');
    },
    onError: () => {
      toast.error('Failed to update whitelist');
    },
  });
}

export function useDeleteWhitelist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteWhitelist,
    onSuccess: () => {
      // Invalidate and refetch whitelists
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.whitelists });
      toast.success('Whitelist deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete whitelist');
    },
  });
}

export function useDownloadWhitelist() {
  const { download, downloadFromApi, isLoading, progress, error } =
    useDownloadFile({
      successMessage: 'Whitelist downloaded successfully',
      errorMessage: 'Failed to download whitelist',
    });

  const downloadWhitelist = async ({
    fileUrl,
    apiEndpoint,
    filename,
  }: {
    fileUrl?: string;
    apiEndpoint?: string;
    filename: string;
  }) => {
    if (apiEndpoint) {
      return await downloadFromApi(apiEndpoint, filename);
    } else if (fileUrl) {
      return await download(fileUrl, filename);
    } else {
      throw new Error('Either fileUrl or apiEndpoint must be provided');
    }
  };

  return {
    mutate: downloadWhitelist,
    mutateAsync: downloadWhitelist,
    isLoading,
    progress,
    error,
  };
}
